/* istanbul ignore file */
// TODO [LEARN-15811]: ^ ignore coverage on this file until we can have app test scaffolding ready
import React, { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grommet } from 'grommet'
import {
  AdvisoryActivityIcons,
  HeaderFour,
  TabButton,
  TabSet,
} from 'secondstep-components'

import { isFlagOn, PUBLISHED_CONTENT_FLAG } from 'utils/featureFlags'
import { useMarkCompleteV3 } from 'hooks/useMarkCompleteV3'
import { buildRegistrationCourseCodeMatchCandidates } from 'utils/lmsHelpers'
import { launchPresentation } from 'utils/playerHelper'
import { resolvePlayerSiteId } from 'utils/playerSiteId'
import HsIndividualResourceActivityDetails from 'components/HsIndividualResourceActivityDetails'
import HsIndividualResourceMetadata from 'components/HsIndividualResourceMetadata'
import HsIndividualResourceReferences from 'components/HsIndividualResourceReferences'
import HsIndividualResourceMarkComplete from 'components/HsIndividualResourceMarkComplete'
import CoursePreviewBanner from 'components/CoursePreviewBanner'
import HighSchoolPreviewBanner from 'components/HighSchoolPreviewBanner'
import TieredInterventionMarkComplete from 'components/TieredInterventionMarkComplete'
import MarkCompleteV3 from 'components/MarkCompleteV3'
import BackLink from 'components/BackLink'
import { getAudienceMetadata, getGradeMetadata } from 'utils/highschoolHelpers'
import { SECTION_PHASES } from 'utils/individualResource'
import {
  extractAdvisoryResourcesFromActivityDetails,
  extractGradeValue,
  extractUnitValue,
  getAdvisoryIconColorByProgram,
  getAdvisoryActivityHref,
  getPhaseSections,
  groupAdvisoryActivitiesByType,
  hasBeforeLearningExperienceSection,
  isAdvisoryActivitiesHeaderBlock,
  getBreadcrumbText,
  normalizePhaseSection,
  getBackPath,
  resolvePresentationLaunchButtonText,
} from './helpers'
import {
  getLanguageToggle,
  resolveCanToggleLanguage,
} from 'components/HsIndividualResourceActivityDetails/helpers'
import { isResourceComplete } from 'utils/resourceCompletionHelpers'
import {
  getCurrentProgram,
  PROGRAM_TYPES,
  getProgramTheme,
} from 'utils/program/helpers'
import { DWB_PROGRAM_TYPE } from 'utils/program/types'
import { resolveCourseNodeId } from 'utils/program/training'
import {
  tabTheme,
  ActivityButton,
  AudienceTagContainer,
  ControlPanel,
  DetailsContainer,
  ExternalLinkIcon,
  HeaderContainer,
  PageContainer,
  StyledHeaderOne,
  SurveyLink,
  BannerSpacer,
  SiteSelectorWrapper,
  HsIndividualResourceMarkCompleteWrapper,
  Divider,
  HeaderTitleContainer,
  TitleWrapper,
  HeaderContentWrapper,
  MarkCompleteContainer,
  MetadataContainer,
  PhaseContentWrapper,
  PhaseSectionBody,
  ResourceContainer,
  BreadcrumbWrapper,
  NavigationWrapper,
  SectionButtonRow,
  SectionContainer,
  SectionHeaderRow,
  SectionTitle,
  StyledMarkdownParagraph,
  ResourceImageContainer,
  PreviewBannerWrapper,
  AdvisoryActivitiesContainer,
  AdvisoryActivitiesTitle,
  AdvisoryActivitiesGrid,
  AdvisoryInlineLink,
  AdvisoryInlineLinkRow,
  AdvisoryInlineLinks,
  PhaseTextLinkWithFileIcon,
  WeeklyPlanContentContainer,
  WeeklyPlanHeaderContainer,
  WeeklyPlanOuterContainer,
} from './component.styles'
import { SURVEY_TEXT, PREVIEW_BUTTON_TEXT } from './constants'
import externalLink from './assets/external_link.svg'
import SiteSelector from 'components/SiteSelector'
import IndividualResourcePrintControls from 'components/IndividualResourcePrintControls'
import ExperiencePlayerLaunchButton from 'components/ExperiencePlayerLaunchButton'
import { getRiseActivityLaunchInfo } from 'components/ExperiencePlayerLaunchButton/helpers'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContextLegacy'
import AdvisoryActivityLink from 'experienceComponents/AdvisoryActivityLink'
import ImageFullWidth from 'experienceComponents/ImageFullWidth'
import HeaderBodyCopy from 'experienceComponents/HeaderBodyCopy'
import ImageBodyCopyFlexible from 'components/ImageBodyCopyFlexible'
import InlineVideo from 'components/InlineVideo'
import LanguageToggle from 'experienceComponents/LanguageToggle'
import contentTypes from 'utils/contentfulParser/contents'
import { toCamelCase, toPascalCase } from 'utils/stringHelpers'
import { LanguageToggleWrapper as SharedLanguageToggleWrapper } from 'components/HsIndividualResourceActivityDetails/component.styles'

const VIDEO_VARIANT = 'highSchool'

const CONTENT_GROUP_TO_INSTRUCTOR_DATA_KEY = {
  dwb: 'Dwb',
  k5seldigital: 'K5SelDigital',
  mspdigital: 'MspDigital',
  highschool: 'HighSchool',
}

const PROGRAM_TO_INSTRUCTOR_DATA_KEY = {
  [PROGRAM_TYPES.ELEMENTARY]: 'K5SelDigital',
  [PROGRAM_TYPES.ELEMENTARY_TIER_2]: 'K5SelDigital',
  [PROGRAM_TYPES.MIDDLE_SCHOOL]: 'MspDigital',
  [PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2]: 'MspDigital',
}

const hasRenderablePhaseSection = section => {
  return Array.isArray(section?.content) && section.content.length > 0
}

const CONTENT_MAPPING = {
  headerBodyCopy: {
    component: HeaderBodyCopy,
    parser: (content, handlers) => ({
      ...contentTypes.headerBodyCopy(content),
      ...handlers,
    }),
  },
  h4: {
    component: HeaderFour,
    parser: content => ({
      children: content?.h4Content,
      dataTestId: content?.h4Content?.toLowerCase()?.replace(' ', '-'),
    }),
  },
  textLinkIcon: {
    component: PhaseTextLinkWithFileIcon,
    parser: contentTypes.textLinkIcon,
  },
  image: {
    component: ImageFullWidth,
    parser: content => ({
      imageThumbnailUrl: content?.image?.file?.url || content?.image?.url,
      imageUrl: content?.image?.file?.url || content?.image?.url,
      title: '',
      isZoomable: true,
    }),
  },
  imageBodyCopyFlexible: {
    component: ImageBodyCopyFlexible,
    parser: content => ({
      ...contentTypes.imageBodyCopyFlexible(content),
      isZoomable: true,
      title: '',
    }),
  },
  video: {
    component: InlineVideo,
    parser: content => ({
      entryId: content?.id,
      locale: content?.locale,
      variant: VIDEO_VARIANT,
    }),
  },
}

const renderContentComponent = ({ contentBlock, key, handlers }) => {
  const normalizedContentType =
    typeof contentBlock?.contentType === 'string'
      ? toCamelCase(contentBlock.contentType)
      : contentBlock?.contentType?.id
  const normalizedSnakeContentType =
    typeof contentBlock?.content_type === 'string'
      ? toCamelCase(contentBlock.content_type)
      : contentBlock?.content_type?.id
  const normalizedType = contentBlock?.type
    ? toCamelCase(contentBlock.type)
    : null

  const mapping =
    CONTENT_MAPPING[normalizedContentType] ||
    CONTENT_MAPPING[normalizedSnakeContentType] ||
    CONTENT_MAPPING[normalizedType] ||
    CONTENT_MAPPING[toCamelCase(contentBlock?.__typename)]

  if (!mapping) {
    return null
  }

  const ContentComponent = mapping.component
  return (
    <ContentComponent key={key} {...mapping.parser(contentBlock, handlers)} />
  )
}

const resolveAdvisoryIcon = advisoryActivity => {
  const iconNameCandidates = [
    advisoryActivity?.advisoryIcon,
    advisoryActivity?.advisoryActivityType,
  ].filter(Boolean)

  for (const iconNameCandidate of iconNameCandidates) {
    const camelCaseName = toCamelCase(iconNameCandidate)
    const singularCamelCaseName = camelCaseName.endsWith('s')
      ? camelCaseName.slice(0, -1)
      : camelCaseName

    const iconFromCandidate =
      AdvisoryActivityIcons[toPascalCase(camelCaseName)] ||
      AdvisoryActivityIcons[toPascalCase(singularCamelCaseName)]

    if (iconFromCandidate) {
      return iconFromCandidate
    }
  }

  return null
}

const isAdvisoryActivityTextLinkBlock = contentBlock => {
  const normalizedContentType =
    typeof contentBlock?.contentType === 'string'
      ? toCamelCase(contentBlock.contentType)
      : contentBlock?.contentType?.id
  const normalizedSnakeContentType =
    typeof contentBlock?.content_type === 'string'
      ? toCamelCase(contentBlock.content_type)
      : contentBlock?.content_type?.id
  const normalizedType = contentBlock?.type
    ? toCamelCase(contentBlock.type)
    : null
  const normalizedTypename = toCamelCase(contentBlock?.__typename)

  return [
    normalizedContentType,
    normalizedSnakeContentType,
    normalizedType,
    normalizedTypename,
  ]
    .filter(Boolean)
    .map(value => String(value).toLowerCase())
    .includes('textlinkicon')
}

const IndividualResource = ({
  coursePointer: coursePointerProp,
  entry,
  gradeInstructorData,
  userContext,
  navigationEvent,
  isHsPreview,
  isPreview,
  productTabTitle,
  match,
  routeContent,
  siteId,
  markCompleteV3Config,
  useLineLength880,
  useSecondaryMarkCompleteButton = true,
}) => {
  if (!entry) return null

  const coursePointer = coursePointerProp ?? routeContent?.coursePointer ?? null

  const {
    activityDetails,
    facetMetadataSetting,
    image,
    markComplete: markCompleteContent,
    presentation,
    references,
    resourceMetadata,
    surveyLink,
    languageToggle: entryLanguageToggle,
  } = entry || {}
  const { changeLocale, locale: routeLocale } = routeContent || {}
  const { metadataValues } = resourceMetadata || {}
  const [activeActivityIndex, setActiveActivityIndex] = useState(0)
  const [reference, setReference] = useState()
  const [openAccordionReference, setOpenAccordionReference] = useState(false)
  const supRef = useRef()
  const hasWarnedNoRegistration = useRef(false)

  // When curriculum-service provides a courseCode via the course pointer, require
  // that at least one siteEducator or educator registration matches it. Without a
  // courseCode we cannot validate, so we accept any existing registrations.
  const hasMatchingRegistration = useMemo(() => {
    if (!gradeInstructorData) return true

    const courseCode =
      coursePointer?.query?.courseCode ||
      coursePointer?.query?.course_code ||
      null

    const siteEducator = Array.isArray(gradeInstructorData.siteEducator)
      ? gradeInstructorData.siteEducator
      : []
    const educator = Array.isArray(gradeInstructorData.educator)
      ? gradeInstructorData.educator
      : []

    if (courseCode) {
      const normalizedCourseCode = courseCode.toLowerCase()
      // Curriculum-service course codes often differ from LMS registration
      // course_name values:
      // - core tier-1: "1_core_tier-1_siteEducator" ↔ registration "1"
      // - tier-2: "3-5_core_tier-2_siteEducator" ↔ "3-5_curriculum_tier2"
      // DWB registrations already store the full curriculum-service code, so
      // the exact-match candidate covers them.
      const matchCandidates = buildRegistrationCourseCodeMatchCandidates(
        normalizedCourseCode,
      )
      const matchesCourseCode = reg => {
        const regCode = String(
          reg?.course_name || reg?.course_code || '',
        ).toLowerCase()
        return matchCandidates.includes(regCode)
      }
      return (
        siteEducator.some(matchesCourseCode) || educator.some(matchesCourseCode)
      )
    }

    // No courseCode from curriculum-service: accept any existing registrations
    return siteEducator.length > 0 || educator.length > 0
  }, [gradeInstructorData, coursePointer])

  useEffect(() => {
    if (
      !gradeInstructorData ||
      hasMatchingRegistration ||
      hasWarnedNoRegistration.current
    )
      return
    hasWarnedNoRegistration.current = true
    console.warn(
      '[IndividualResourceV3] No registration found in instructorData; the mark complete button will not be shown.',
    )
  }, [gradeInstructorData, hasMatchingRegistration])

  const scrollToBottom = () => {
    setTimeout(() => supRef.current.scrollIntoView({ behavior: 'smooth' }), 500)
  }

  const { description, displayTitle } = resourceMetadata || {}
  const courseNodeId = resolveCourseNodeId({
    route: routeContent?.route,
    article: resourceMetadata,
  })
  const lessonSlug =
    match?.params?.lesson || resourceMetadata?.routeSlug || null
  const audience = getAudienceMetadata(resourceMetadata)
  const grades = getGradeMetadata(resourceMetadata)
  const currentProgram = getCurrentProgram()
  const isHighSchool = currentProgram === PROGRAM_TYPES.HIGH_SCHOOL
  const activityDetailsList = Array.isArray(activityDetails)
    ? activityDetails
    : []
  const hasActivityDetails = activityDetailsList.length > 0
  const activeActivityDetail =
    activityDetailsList[activeActivityIndex] || activityDetailsList[0] || null
  const isDwbPage = routeContent?.contentGroupCode === DWB_PROGRAM_TYPE
  const configuredMarkCompleteV3 =
    markCompleteV3Config?.enabled &&
    (markCompleteV3Config.contentGroupCode || routeContent?.contentGroupCode) &&
    (markCompleteV3Config.instructorDataKey || 'Dwb')
      ? {
          contentGroupCode:
            markCompleteV3Config.contentGroupCode ||
            routeContent?.contentGroupCode,
          instructorDataKey: markCompleteV3Config.instructorDataKey || 'Dwb',
        }
      : null

  const resolvedPresentation =
    presentation ||
    activeActivityDetail?.presentation ||
    resourceMetadata?.presentation ||
    entry?.metadata?.presentation ||
    null
  const resolvedMarkCompleteContent =
    markCompleteContent ||
    activeActivityDetail?.markComplete ||
    resourceMetadata?.markComplete ||
    entry?.metadata?.markComplete ||
    (isDwbPage ? {} : null)
  const resolvedLanguageToggle =
    getLanguageToggle(activeActivityDetail) || entryLanguageToggle || null
  const canToggleLanguage = resolveCanToggleLanguage(resolvedLanguageToggle)
  const shouldShowLanguageToggle = !!(canToggleLanguage && changeLocale)

  const handleReferences = refClicked => {
    const referenceClicked = refClicked.replace(/[^\w\s]/gi, '')
    if (refClicked && referenceClicked) {
      setOpenAccordionReference(true)
      setReference(referenceClicked)
      scrollToBottom()
    }
  }

  const handleActivityTabs = activityIndex => {
    if (openAccordionReference) {
      setOpenAccordionReference(!openAccordionReference)
    }
    setReference(null)
    setActiveActivityIndex(activityIndex)
  }

  useEffect(() => {
    if (displayTitle) {
      document.title = `${productTabTitle} | ${displayTitle}`
    }
  }, [displayTitle, productTabTitle])

  const shouldShowMarkCompleteHs = ({ markCompleteContent, isPreview }) => {
    if (isPreview) return false
    if (!markCompleteContent) return false
    // DWB routes use the curriculum-service-backed MarkCompleteV3 flow.
    if (isDwbPage) return false
    if (configuredMarkCompleteV3) return false
    if (currentProgram !== PROGRAM_TYPES.HIGH_SCHOOL) return false
    if (!hasMatchingRegistration) return false
    return true
  }

  const isK8Program =
    currentProgram === PROGRAM_TYPES.ELEMENTARY ||
    currentProgram === PROGRAM_TYPES.MIDDLE_SCHOOL ||
    currentProgram === PROGRAM_TYPES.ELEMENTARY_TIER_2 ||
    currentProgram === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2

  const isK8Tier2Program =
    currentProgram === PROGRAM_TYPES.ELEMENTARY_TIER_2 ||
    currentProgram === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2

  const resolvedMarkCompleteV3ContentGroupCode =
    configuredMarkCompleteV3?.contentGroupCode ||
    routeContent?.contentGroupCode ||
    null

  // Curriculum-service-backed mark complete for DWB and all K-8 lesson pages
  // (tier-1 and tier-2) that have a coursePointer from the content HOC.
  const shouldShowMarkCompleteV3 = ({ isPreview }) => {
    if (isPreview) return false
    if (!coursePointer && !configuredMarkCompleteV3) return false
    if (!hasMatchingRegistration) return false
    // MarkCompleteV3 requires a contentGroupCode for course-tree/registration
    // lookups; do not render without one.
    if (!resolvedMarkCompleteV3ContentGroupCode) return false
    if (isDwbPage || !!configuredMarkCompleteV3) return true
    return isK8Program && !!coursePointer
  }

  // Fallback for genuine tier-2 pages that do not yet have a coursePointer.
  // Prefer MarkCompleteV3 above whenever curriculum-service data is available.
  const shouldShowTieredInterventionsMarkComplete = ({ isPreview }) => {
    if (isPreview) return false
    if (configuredMarkCompleteV3) return false
    if (isDwbPage) return false
    if (!isK8Tier2Program) return false
    if (coursePointer) return false
    if (!hasMatchingRegistration) return false
    return true
  }

  const showsMarkCompleteV3 = shouldShowMarkCompleteV3({ isPreview })
  const showsTieredInterventionMarkComplete = shouldShowTieredInterventionsMarkComplete(
    { isPreview },
  )
  const showsHsMarkComplete = shouldShowMarkCompleteHs({
    markCompleteContent: resolvedMarkCompleteContent,
    isPreview,
  })
  const markCompleteV3Props = {
    contentGroupCode: resolvedMarkCompleteV3ContentGroupCode,
    instructorDataKey:
      configuredMarkCompleteV3?.instructorDataKey ||
      CONTENT_GROUP_TO_INSTRUCTOR_DATA_KEY[
        String(resolvedMarkCompleteV3ContentGroupCode || '').toLowerCase()
      ] ||
      PROGRAM_TO_INSTRUCTOR_DATA_KEY[currentProgram] ||
      'Dwb',
    lessonSlug,
  }

  // K-8 and DWB teach launches go through LMS so the player can track progress,
  // show the mark-done slide, and sync Teach/Resume/Review + Mark Done state.
  // Preview always stays on the standalone player.
  const usesLmsPresentationLaunch = !isPreview && (isDwbPage || isK8Program)
  const {
    registrationId: lmsRegistrationId,
    leafNodeId: lmsLeafNodeId,
    attemptStatus: lmsAttemptStatus,
  } = useMarkCompleteV3({
    coursePointer: usesLmsPresentationLaunch ? coursePointer : null,
    courseNodeId: usesLmsPresentationLaunch ? courseNodeId : null,
    contentGroupCode: usesLmsPresentationLaunch
      ? markCompleteV3Props.contentGroupCode
      : null,
    instructorDataKey: usesLmsPresentationLaunch
      ? markCompleteV3Props.instructorDataKey
      : null,
    lessonSlug: usesLmsPresentationLaunch
      ? markCompleteV3Props.lessonSlug
      : null,
    siteId,
  })
  const canLaunchViaLms = !!(lmsRegistrationId && lmsLeafNodeId)
  const presentationLaunchButtonText = resolvePresentationLaunchButtonText({
    usesLmsLaunchLabels: usesLmsPresentationLaunch && canLaunchViaLms,
    attemptStatus: lmsAttemptStatus,
    isHighSchool,
  })

  const launchActivity = (isPreviewLaunch = false) => {
    if (!resolvedPresentation?.id) return
    const { id } = resolvedPresentation
    const theme = getProgramTheme()
    const lessonTitle = resourceMetadata?.routeSlug
    const playerSiteId = resolvePlayerSiteId({ siteId, userContext })

    const contentGroupCode = routeContent?.contentGroupCode || DWB_PROGRAM_TYPE
    const resolutionMode = playerSiteId ? 'singleSite' : 'multiSite'
    let curriculumServiceParams = '&useCurriculumService=true'
    curriculumServiceParams += `&contentGroupCode=${encodeURIComponent(
      contentGroupCode,
    )}`
    curriculumServiceParams += `&resolutionMode=${resolutionMode}`
    if (!isFlagOn([PUBLISHED_CONTENT_FLAG])) {
      curriculumServiceParams += `&draftMode=true`
    }

    // tier=2 is a player content-path hint for tier-2 curriculum, not a
    // synonym for preview mode (matches IndividualResourceV2).
    const baseExtraParams = isPreviewLaunch && isK8Tier2Program ? '&tier=2' : ''
    const extraQueryParams = baseExtraParams + curriculumServiceParams
    const shouldLaunchViaLms =
      !isPreviewLaunch && usesLmsPresentationLaunch && canLaunchViaLms

    if (shouldLaunchViaLms) {
      launchPresentation(
        {
          entryId: id,
          locale: 'en-US',
          mode: 'teach',
          navigationEvent,
          theme,
          lessonTitle,
          extraQueryParams,
          siteId: playerSiteId,
          skipUserContext: true,
          v2_class_registration_id: lmsRegistrationId,
          leaf_node_id: lmsLeafNodeId,
        },
        true,
      )
      return
    }

    if (usesLmsPresentationLaunch && !isPreviewLaunch) {
      console.warn(
        '[IndividualResourceV3] Expected LMS launch but registrationId/leafNodeId were missing; falling back to standalone player.',
        {
          registrationId: lmsRegistrationId,
          leafNodeId: lmsLeafNodeId,
        },
      )
    }

    launchPresentation({
      entryId: id,
      locale: 'en-US',
      mode: isPreviewLaunch ? 'preview' : '',
      navigationEvent,
      theme,
      lessonTitle,
      extraQueryParams,
      siteId: playerSiteId,
      rawToken: true,
      skipUserContext: true,
    })
  }

  const gradeValue = extractGradeValue(metadataValues)
  const unitValue = extractUnitValue(metadataValues)
  const audienceTypeTag = unitValue
  const audienceGradeTag = isHighSchool ? grades : gradeValue?.toUpperCase()
  const phaseSections = getPhaseSections({
    activityDetailsList,
    activeActivityDetail,
    entry,
  })
  const beforeSection = phaseSections[SECTION_PHASES.BEFORE]
  const duringSection = phaseSections[SECTION_PHASES.DURING]
  const afterSection = phaseSections[SECTION_PHASES.AFTER]
  const normalizedBeforeSection = normalizePhaseSection({
    section: beforeSection,
  })
  const normalizedDuringSection = normalizePhaseSection({
    section: duringSection,
  })
  const normalizedAfterSection = normalizePhaseSection({
    section: afterSection,
  })
  const hasBeforeSection = hasRenderablePhaseSection(normalizedBeforeSection)
  const hasDuringSection = hasRenderablePhaseSection(normalizedDuringSection)
  const hasAfterSection = hasRenderablePhaseSection(normalizedAfterSection)
  // The before preview button should only render when the payload explicitly
  // defines a LearningExperienceSection with type "before". Memoized to avoid
  // deep-scanning the (potentially large) entry payload on every render.
  const hasBeforePreviewSection = useMemo(
    () => hasBeforeLearningExperienceSection(entry),
    [entry],
  )
  const hasPhaseSections = !!(
    hasBeforeSection ||
    hasDuringSection ||
    hasAfterSection
  )
  // Only learning experiences with a Rise presentation (riseActivity) expose
  // the experience-player launch button.
  const riseActivityLaunchInfo = getRiseActivityLaunchInfo(entry)
  const hasRisePresentation = !!riseActivityLaunchInfo?.lessonId
  // Show the before preview button when the payload defines a before
  // LearningExperienceSection alongside something launchable, OR (only in
  // preview mode) when a Rise activity is available. Rise learning experiences
  // do not return a before-section marker, so the preview-mode fallback is not
  // gated on it.
  const showBeforePreviewButton =
    (hasBeforePreviewSection &&
      (!!resolvedPresentation || hasRisePresentation)) ||
    (isPreview && hasRisePresentation)
  const advisoryResources =
    Array.isArray(entry?.additionalResources) &&
    entry.additionalResources.length
      ? entry.additionalResources
      : extractAdvisoryResourcesFromActivityDetails({
          activityDetailsList,
        })

  const advisoryActivityGroups = groupAdvisoryActivitiesByType(
    advisoryResources,
  )
  const flattenedAdvisoryActivities = Object.values(
    advisoryActivityGroups,
  ).flat()
  const showAdvisoryActivities = Object.keys(advisoryActivityGroups).length > 0
  const advisoryIconColor = getAdvisoryIconColorByProgram(currentProgram)
  const advisoryActivitiesHeaderIndex = useMemo(() => {
    if (!showAdvisoryActivities) return -1
    return (normalizedAfterSection?.content || []).findIndex(contentBlock =>
      isAdvisoryActivitiesHeaderBlock(contentBlock),
    )
  }, [showAdvisoryActivities, normalizedAfterSection])
  const breadcrumbText = getBreadcrumbText(currentProgram, productTabTitle)

  return (
    <PageContainer className="individual-resource-print-page">
      {isHsPreview && (
        <PreviewBannerWrapper>
          <BannerSpacer />
          <HighSchoolPreviewBanner />
        </PreviewBannerWrapper>
      )}
      {isPreview && !isHsPreview && (
        <PreviewBannerWrapper>
          <BannerSpacer />
          <CoursePreviewBanner />
        </PreviewBannerWrapper>
      )}

      <NavigationWrapper data-print-hidden="navigation">
        {(!isHighSchool || isDwbPage) && (
          <BreadcrumbWrapper>
            <BackLink
              dataTestId="breadcrumb"
              onClick={() =>
                navigationEvent(
                  getBackPath(window.location.pathname, window.location.search),
                )
              }
            >
              {breadcrumbText}
            </BackLink>
          </BreadcrumbWrapper>
        )}
        <SiteSelectorWrapper isHighSchool={isHighSchool}>
          <IndividualResourcePrintControls program={currentProgram} />
          <SiteSelector />
        </SiteSelectorWrapper>
      </NavigationWrapper>
      <HeaderContainer>
        <div className="content">
          <HeaderContentWrapper isHighSchool={isHighSchool}>
            <AudienceTagContainer
              audience={audience}
              isHighSchool={isHighSchool}
            >
              {!!audienceTypeTag && <div>{audienceTypeTag}</div>}
              {!!audienceGradeTag && <div>{audienceGradeTag}</div>}
            </AudienceTagContainer>
            <HeaderTitleContainer>
              <TitleWrapper>
                <StyledHeaderOne>{displayTitle}</StyledHeaderOne>
              </TitleWrapper>
              {!hasPhaseSections && showsMarkCompleteV3 && (
                <div data-print-hidden="mark-done">
                  <MarkCompleteV3
                    contentGroupCode={markCompleteV3Props.contentGroupCode}
                    courseNodeId={courseNodeId}
                    coursePointer={coursePointer}
                    instructorDataKey={markCompleteV3Props.instructorDataKey}
                    lessonSlug={markCompleteV3Props.lessonSlug}
                    siteId={siteId}
                    useSecondaryMarkCompleteButton={
                      useSecondaryMarkCompleteButton
                    }
                  />
                </div>
              )}
              {!hasPhaseSections && showsTieredInterventionMarkComplete && (
                <div data-print-hidden="mark-done">
                  <TieredInterventionMarkComplete
                    allowAnyRegistrationMatch
                    courseNodeId={courseNodeId}
                    instructorData={gradeInstructorData}
                    isComplete={isResourceComplete({
                      allowAnyRegistrationMatch: true,
                      instructorData: gradeInstructorData,
                      courseNodeId,
                      program: currentProgram,
                    })}
                    match={match}
                    program={currentProgram}
                    strictCompletionUpdate
                    useProgramFlexRefresh
                    useSecondaryMarkCompleteButton={
                      useSecondaryMarkCompleteButton
                    }
                    userContext={userContext}
                  />
                </div>
              )}
            </HeaderTitleContainer>
            <StyledMarkdownParagraph
              $lineLength880={useLineLength880}
              isHighSchool={isHighSchool}
              small
            >
              {description}
            </StyledMarkdownParagraph>
            {!!surveyLink && (
              <SurveyLink
                href={surveyLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                {SURVEY_TEXT}
                <ExternalLinkIcon
                  alt={'External Link Icon'}
                  src={externalLink}
                />
              </SurveyLink>
            )}
            {!hasPhaseSections && showsHsMarkComplete && (
              <HsIndividualResourceMarkCompleteWrapper data-print-hidden="mark-done">
                <HsIndividualResourceMarkComplete
                  activityType={audience}
                  courseNodeId={courseNodeId}
                  isV3
                  markCompleteContent={resolvedMarkCompleteContent}
                  practiceName={displayTitle}
                />
              </HsIndividualResourceMarkCompleteWrapper>
            )}
          </HeaderContentWrapper>
          {isHighSchool && !!image?.url && (
            <ResourceImageContainer
              alt={image?.description}
              className="resource-image"
              src={image?.url}
            />
          )}
        </div>
      </HeaderContainer>
      <ResourceContainer isHighSchool={isHighSchool}>
        <MetadataContainer isHighSchool={isHighSchool}>
          <HsIndividualResourceMetadata
            audience={audience}
            disableOuterBackground
            facetMetadataSetting={facetMetadataSetting}
            program={currentProgram}
            resourceMetadata={resourceMetadata}
          />
        </MetadataContainer>
        {isHighSchool && !hasPhaseSections && (
          <ControlPanel>
            <div className="content">
              {hasActivityDetails && (
                <Grommet theme={tabTheme}>
                  <TabSet>
                    {activityDetailsList.map((activity, index) => (
                      <TabButton
                        dataTestId={`${activity.displayTitle
                          .toLowerCase()
                          .replace(' ', '-')}-tab`}
                        isActive={index === activeActivityIndex}
                        key={`${activity.displayTitle.toLowerCase()}-${index}`}
                        label={activity.displayTitle}
                        onClick={() => handleActivityTabs(index)}
                      />
                    ))}
                  </TabSet>
                </Grommet>
              )}
              {!!resolvedPresentation && !isPreview && (
                <ActivityButton
                  $isHighSchoolButton={isHighSchool}
                  onClick={() => launchActivity(false)}
                >
                  {presentationLaunchButtonText}
                </ActivityButton>
              )}
            </div>
          </ControlPanel>
        )}
        <DetailsContainer audience={audience} isHighSchool={isHighSchool}>
          <div className="content">
            {hasPhaseSections ? (
              <WeeklyPlanOuterContainer
                audience={audience}
                isHighSchool={isHighSchool}
              >
                {!isHighSchool && (
                  <WeeklyPlanHeaderContainer>
                    Lesson Plan
                  </WeeklyPlanHeaderContainer>
                )}
                <WeeklyPlanContentContainer>
                  <PhaseContentWrapper>
                    {(hasBeforeSection || showBeforePreviewButton) && (
                      <SectionContainer>
                        {shouldShowLanguageToggle && (
                          <SectionButtonRow
                            $alignLeft
                            $compact
                            data-print-hidden="before-language-toggle-row"
                          >
                            <SharedLanguageToggleWrapper
                              data-print-hidden="language-toggle"
                              locale={routeLocale}
                            >
                              <LanguageToggle
                                boundary={canToggleLanguage}
                                getEntryWithNewLocale={changeLocale}
                                locale={routeLocale}
                              />
                            </SharedLanguageToggleWrapper>
                          </SectionButtonRow>
                        )}
                        <SectionHeaderRow>
                          <SectionTitle>
                            {normalizedBeforeSection?.displayTitle}
                          </SectionTitle>
                          {showBeforePreviewButton && (
                            <SectionButtonRow
                              $alignLeft
                              data-print-hidden="before-controls-row"
                            >
                              {hasRisePresentation ? (
                                <ExperiencePlayerLaunchButton
                                  dataTestId="preview-experience-button"
                                  expiration={riseActivityLaunchInfo.expiration}
                                  forceFixedWidth
                                  isHighSchool={isHighSchool}
                                  label={PREVIEW_BUTTON_TEXT}
                                  lessonId={riseActivityLaunchInfo.lessonId}
                                />
                              ) : (
                                <ActivityButton
                                  $isHighSchoolButton
                                  onClick={() => launchActivity(true)}
                                >
                                  {PREVIEW_BUTTON_TEXT}
                                </ActivityButton>
                              )}
                            </SectionButtonRow>
                          )}
                        </SectionHeaderRow>
                        {hasBeforeSection && (
                          <PhaseSectionBody
                            $lineLength880={useLineLength880}
                            isHighSchool={isHighSchool}
                          >
                            {(normalizedBeforeSection?.content || []).map(
                              (content, index) =>
                                renderContentComponent({
                                  contentBlock: content,
                                  key: `before-${index}`,
                                  handlers: {
                                    referenceHandler: handleReferences,
                                    presentation: resolvedPresentation,
                                    launchActivity,
                                    isPreview,
                                  },
                                }),
                            )}
                          </PhaseSectionBody>
                        )}
                      </SectionContainer>
                    )}

                    {hasDuringSection && (
                      <>
                        {hasBeforeSection && <Divider />}
                        <SectionContainer>
                          <SectionHeaderRow>
                            <SectionTitle>
                              {normalizedDuringSection?.displayTitle}
                            </SectionTitle>
                            <SectionButtonRow
                              $alignTop
                              data-print-hidden="during-controls-row"
                            >
                              {hasRisePresentation && !isPreview && (
                                <ExperiencePlayerLaunchButton
                                  dataTestId="launch-experience-button"
                                  expiration={riseActivityLaunchInfo.expiration}
                                  isHighSchool={isHighSchool}
                                  label={presentationLaunchButtonText}
                                  lessonId={riseActivityLaunchInfo.lessonId}
                                />
                              )}
                              {!!resolvedPresentation &&
                                !isPreview &&
                                !hasRisePresentation && (
                                  <ActivityButton
                                    // Match Mark Done width (9.375rem) beside it in the during row.
                                    $isHighSchoolButton
                                    onClick={() => launchActivity(false)}
                                  >
                                    {presentationLaunchButtonText}
                                  </ActivityButton>
                                )}
                              {showsMarkCompleteV3 && (
                                <MarkCompleteContainer
                                  $isHighSchool={isHighSchool}
                                  data-print-hidden="mark-done"
                                >
                                  <MarkCompleteV3
                                    contentGroupCode={
                                      markCompleteV3Props.contentGroupCode
                                    }
                                    courseNodeId={courseNodeId}
                                    coursePointer={coursePointer}
                                    instructorDataKey={
                                      markCompleteV3Props.instructorDataKey
                                    }
                                    lessonSlug={markCompleteV3Props.lessonSlug}
                                    siteId={siteId}
                                    useSecondaryMarkCompleteButton={
                                      useSecondaryMarkCompleteButton
                                    }
                                  />
                                </MarkCompleteContainer>
                              )}
                              {!isHighSchool &&
                                showsTieredInterventionMarkComplete && (
                                  <MarkCompleteContainer
                                    $isHighSchool={isHighSchool}
                                    data-print-hidden="mark-done"
                                  >
                                    <TieredInterventionMarkComplete
                                      allowAnyRegistrationMatch
                                      courseNodeId={courseNodeId}
                                      instructorData={gradeInstructorData}
                                      isComplete={isResourceComplete({
                                        allowAnyRegistrationMatch: true,
                                        instructorData: gradeInstructorData,
                                        courseNodeId,
                                        program: currentProgram,
                                      })}
                                      match={match}
                                      program={currentProgram}
                                      strictCompletionUpdate
                                      useProgramFlexRefresh
                                      useSecondaryMarkCompleteButton={
                                        useSecondaryMarkCompleteButton
                                      }
                                      userContext={userContext}
                                    />
                                  </MarkCompleteContainer>
                                )}
                              {isHighSchool && showsHsMarkComplete && (
                                <MarkCompleteContainer
                                  $isHighSchool={isHighSchool}
                                  data-print-hidden="mark-done"
                                >
                                  <HsIndividualResourceMarkComplete
                                    activityType={audience}
                                    courseNodeId={courseNodeId}
                                    isInline
                                    isV3
                                    markCompleteContent={
                                      resolvedMarkCompleteContent
                                    }
                                    practiceName={displayTitle}
                                  />
                                </MarkCompleteContainer>
                              )}
                            </SectionButtonRow>
                          </SectionHeaderRow>
                          <PhaseSectionBody
                            $lineLength880={useLineLength880}
                            isHighSchool={isHighSchool}
                          >
                            {(normalizedDuringSection?.content || []).map(
                              (content, index) =>
                                renderContentComponent({
                                  contentBlock: content,
                                  key: `during-${index}`,
                                  handlers: {
                                    referenceHandler: handleReferences,
                                    presentation: resolvedPresentation,
                                    launchActivity,
                                    isPreview,
                                  },
                                }),
                            )}
                          </PhaseSectionBody>
                        </SectionContainer>
                      </>
                    )}

                    {hasAfterSection && (
                      <>
                        {(hasBeforeSection || hasDuringSection) && <Divider />}
                        <SectionContainer>
                          <SectionHeaderRow>
                            <SectionTitle>
                              {normalizedAfterSection?.displayTitle}
                            </SectionTitle>
                          </SectionHeaderRow>
                          <PhaseSectionBody
                            $lineLength880={useLineLength880}
                            isHighSchool={isHighSchool}
                          >
                            {(normalizedAfterSection?.content || []).map(
                              (content, index) => {
                                const shouldHideDuplicatedAdvisoryLink =
                                  advisoryActivitiesHeaderIndex > -1 &&
                                  index > advisoryActivitiesHeaderIndex &&
                                  isAdvisoryActivityTextLinkBlock(content)
                                return (
                                  <React.Fragment
                                    key={`after-fragment-${index}`}
                                  >
                                    {!shouldHideDuplicatedAdvisoryLink &&
                                      renderContentComponent({
                                        contentBlock: content,
                                        key: `after-${index}`,
                                        handlers: {
                                          referenceHandler: handleReferences,
                                          presentation: resolvedPresentation,
                                          launchActivity,
                                          isPreview,
                                        },
                                      })}
                                    {showAdvisoryActivities &&
                                      isAdvisoryActivitiesHeaderBlock(
                                        content,
                                      ) && (
                                        <AdvisoryInlineLinks>
                                          {flattenedAdvisoryActivities.map(
                                            activity => {
                                              const advisoryHref = getAdvisoryActivityHref(
                                                {
                                                  productName:
                                                    match?.params?.program ||
                                                    'middle-school',
                                                  activity,
                                                },
                                              )
                                              if (!advisoryHref) {
                                                return null
                                              }

                                              const AdvisoryIcon = resolveAdvisoryIcon(
                                                activity,
                                              )

                                              return (
                                                <AdvisoryInlineLinkRow
                                                  iconColor={advisoryIconColor}
                                                  key={activity.id}
                                                >
                                                  {!!AdvisoryIcon && (
                                                    <span className="advisory-inline-icon">
                                                      <AdvisoryIcon />
                                                    </span>
                                                  )}
                                                  <AdvisoryInlineLink
                                                    href={advisoryHref}
                                                  >
                                                    {activity.displayTitle}
                                                  </AdvisoryInlineLink>
                                                </AdvisoryInlineLinkRow>
                                              )
                                            },
                                          )}
                                        </AdvisoryInlineLinks>
                                      )}
                                  </React.Fragment>
                                )
                              },
                            )}
                          </PhaseSectionBody>
                        </SectionContainer>
                      </>
                    )}
                  </PhaseContentWrapper>
                </WeeklyPlanContentContainer>
              </WeeklyPlanOuterContainer>
            ) : (
              !!activityDetailsList[activeActivityIndex] && (
                <HsIndividualResourceActivityDetails
                  audience={audience}
                  details={activityDetailsList[activeActivityIndex]}
                  getEntryWithNewLocale={changeLocale}
                  isPreview={isPreview}
                  languageToggle={
                    getLanguageToggle(
                      activityDetailsList[activeActivityIndex],
                    ) ||
                    entryLanguageToggle ||
                    null
                  }
                  launchActivity={launchActivity}
                  launchButtonText={presentationLaunchButtonText}
                  locale={routeLocale}
                  presentation={resolvedPresentation}
                  program={currentProgram}
                  referenceHandler={handleReferences}
                />
              )
            )}
            {!hasPhaseSections && showAdvisoryActivities && (
              <AdvisoryActivitiesContainer>
                <AdvisoryActivitiesTitle>
                  Advisory Activity Links
                </AdvisoryActivitiesTitle>
                <AdvisoryActivitiesGrid>
                  {Object.keys(advisoryActivityGroups).map(advisoryType => (
                    <AdvisoryActivityLink
                      advisoryType={advisoryActivityGroups[advisoryType]}
                      key={advisoryType}
                      productName={match?.params?.program || 'middle-school'}
                    />
                  ))}
                </AdvisoryActivitiesGrid>
              </AdvisoryActivitiesContainer>
            )}
            {!!references && (
              <HsIndividualResourceReferences
                audience={audience}
                focusedReference={reference}
                isOpen={openAccordionReference}
                ref={supRef}
                references={references}
                setAccordionCollapse={setOpenAccordionReference}
              />
            )}
          </div>
        </DetailsContainer>
      </ResourceContainer>
    </PageContainer>
  )
}

IndividualResource.propTypes = {
  coursePointer: PropTypes.shape({
    href: PropTypes.string,
    method: PropTypes.string,
    query: PropTypes.object,
  }),
  entry: PropTypes.object,
  gradeInstructorData: PropTypes.object,
  isHsPreview: PropTypes.bool,
  isPreview: PropTypes.bool,
  markCompleteV3Config: PropTypes.shape({
    enabled: PropTypes.bool,
    contentGroupCode: PropTypes.string,
    instructorDataKey: PropTypes.string,
  }),
  match: PropTypes.func,
  navigationEvent: PropTypes.func,
  productTabTitle: PropTypes.string,
  routeContent: PropTypes.object,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  useLineLength880: PropTypes.bool,
  useSecondaryMarkCompleteButton: PropTypes.bool,
  userContext: PropTypes.object,
}

export default withSiteContext(IndividualResource, {
  useGuard: true,
})
